import { useState } from 'react';
import { AlertTriangle, ArrowDown, Check, ChevronDown, ChevronUp, Clock, GripVertical, X } from 'lucide-react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { DeliveryPolicySettings, DeliverySettings, FallbackRule, QuietHoursSettings } from '@/api/settings';
import { DispatchModeBadge } from '@/features/templates/dispatchMode';
import { fallbackCanvas } from '@/features/delivery/fallbackCanvas';
import type { FallbackSectionId } from '@/features/delivery/fallbackSections';

const CHANNEL_METADATA: Record<string, { name: string; hasTracking: boolean; defaultTimeout?: number }> = {
  whatsapp_waba: { name: 'WhatsApp WABA', hasTracking: true },
  whatsapp_regular: { name: 'WhatsApp Regular', hasTracking: true },
  telegram_client: { name: 'Telegram Client', hasTracking: false, defaultTimeout: 24 },
  telegram_bot: { name: 'Telegram Bot', hasTracking: false, defaultTimeout: 24 },
  max: { name: 'MAX', hasTracking: false, defaultTimeout: 24 },
  sms: { name: 'SMS', hasTracking: true },
  sms_tel: { name: 'SMS-Tel', hasTracking: true },
};

const DELIVERY_CHANNELS = Object.keys(CHANNEL_METADATA);
const FALLBACK_RULE_ORDER = ['failed', 'sent_not_delivered', 'delivered_not_read', 'action_not_completed'];
const FALLBACK_RULE_DESCRIPTIONS: Record<string, string> = {
  delivered_not_read: 'Доставлено, но пациент не прочитал',
  sent_not_delivered: 'Отправлено, но не доставлено',
  failed: 'Жёсткая ошибка отправки',
  action_not_completed: 'Нет реакции на CTA / OKDoc',
};

interface DeliverySettingsEditorProps {
  settings: DeliverySettings;
  onChange: (next: DeliverySettings) => void;
  section: FallbackSectionId;
  onSectionChange: (section: FallbackSectionId) => void;
}

function parseNumericInput(value: string, fallback: number): number {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function cloneFallbackRule(rule: FallbackRule): FallbackRule {
  return { ...rule };
}

function clonePolicy(policy: DeliveryPolicySettings): DeliveryPolicySettings {
  return {
    ...policy,
    channel_priority: [...policy.channel_priority],
    disabled_channels: [...(policy.disabled_channels || [])],
    fallback_rules: (policy.fallback_rules || []).map(cloneFallbackRule),
  };
}

function defaultFallbackRule(trigger: string): FallbackRule {
  return {
    trigger,
    timeout_hours: trigger === 'failed' ? 0 : trigger === 'action_not_completed' ? 24 : 1,
    next_channel: 'auto',
    description: FALLBACK_RULE_DESCRIPTIONS[trigger] || trigger,
  };
}

function formatFallbackRuleSummary(rule: FallbackRule): string {
  const label = FALLBACK_RULE_DESCRIPTIONS[rule.trigger] || rule.trigger;
  if (rule.trigger === 'failed') {
    return `${label} -> ${rule.next_channel || 'auto'}`;
  }
  return `${label}: ${rule.timeout_hours}ч -> ${rule.next_channel || 'auto'}`;
}

function buildClinicPolicy(settings: DeliverySettings): DeliveryPolicySettings {
  return {
    channel_priority: settings.channel_priority,
    disabled_channels: settings.disabled_channels || [],
    fallback_rules: settings.fallback_rules,
    max_fallback_depth: settings.max_fallback_depth,
    cooldown_minutes: settings.cooldown_minutes,
  };
}

function moveItem<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (toIndex < 0 || toIndex >= items.length || fromIndex === toIndex) {
    return items;
  }
  const next = [...items];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
}

export function DeliverySettingsEditor({
  settings,
  onChange,
  section,
  onSectionChange,
}: DeliverySettingsEditorProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const updateSettings = (patch: Partial<DeliverySettings>) => {
    onChange({ ...settings, ...patch });
  };

  const clinicPolicy = buildClinicPolicy(settings);
  const broadcastGroups = settings.broadcast_groups || [];
  const disabledFallbackGroups = settings.disable_fallback_groups || [];
  const groupPolicies = settings.broadcast_group_delivery_settings || {};

  const getEffectiveGroupPolicy = (group: string): { policy: DeliveryPolicySettings; isOverride: boolean } => {
    const override = groupPolicies[group];
    if (override) {
      return { policy: clonePolicy(override), isOverride: true };
    }
    return { policy: clonePolicy(clinicPolicy), isOverride: false };
  };

  const setGroupPolicy = (
    group: string,
    updater: (current: DeliveryPolicySettings) => DeliveryPolicySettings,
  ) => {
    const { policy } = getEffectiveGroupPolicy(group);
    const nextPolicies = {
      ...groupPolicies,
      [group]: updater(clonePolicy(policy)),
    };
    updateSettings({ broadcast_group_delivery_settings: nextPolicies });
  };

  const resetGroupPolicyOverride = (group: string) => {
    const nextPolicies = { ...groupPolicies };
    delete nextPolicies[group];
    updateSettings({
      broadcast_group_delivery_settings: Object.keys(nextPolicies).length > 0 ? nextPolicies : undefined,
    });
  };

  const toggleClinicChannelEnabled = (channel: string) => {
    const disabledChannels = settings.disabled_channels || [];
    const isDisabled = disabledChannels.includes(channel);
    updateSettings({
      disabled_channels: isDisabled
        ? disabledChannels.filter((candidate) => candidate !== channel)
        : [...disabledChannels, channel],
    });
  };

  const updateClinicFallbackRule = (trigger: string, patch: Partial<FallbackRule>) => {
    const ruleIndex = settings.fallback_rules.findIndex((rule) => rule.trigger === trigger);
    if (ruleIndex >= 0) {
      const nextRules = [...settings.fallback_rules];
      nextRules[ruleIndex] = {
        ...nextRules[ruleIndex],
        ...patch,
        trigger,
        description: FALLBACK_RULE_DESCRIPTIONS[trigger] || nextRules[ruleIndex].description,
        next_channel: patch.next_channel ?? (nextRules[ruleIndex].next_channel || 'auto'),
      };
      updateSettings({ fallback_rules: nextRules });
      return;
    }

    updateSettings({
      fallback_rules: [
        ...settings.fallback_rules,
        {
          ...defaultFallbackRule(trigger),
          ...patch,
          trigger,
        },
      ],
    });
  };

  const updateQuietHours = (field: keyof QuietHoursSettings, value: boolean | number) => {
    updateSettings({
      quiet_hours: { ...settings.quiet_hours, [field]: value },
    });
  };

  const updateBroadcastGroupMode = (group: string, mode: string) => {
    updateSettings({
      broadcast_group_modes: {
        ...(settings.broadcast_group_modes || {}),
        [group]: mode,
      },
    });
  };

  const updateBroadcastGroupLimit = (group: string, value: number) => {
    updateSettings({
      broadcast_group_daily_limits: {
        ...(settings.broadcast_group_daily_limits || {}),
        [group]: value,
      },
    });
  };

  const toggleFallbackDisabledGroup = (group: string) => {
    const next = disabledFallbackGroups.includes(group)
      ? disabledFallbackGroups.filter((item) => item !== group)
      : [...disabledFallbackGroups, group];
    updateSettings({ disable_fallback_groups: next });
  };

  const toggleGroupChannelEnabled = (group: string, channel: string) => {
    setGroupPolicy(group, (current) => {
      const disabledChannels = current.disabled_channels || [];
      const isDisabled = disabledChannels.includes(channel);
      return {
        ...current,
        disabled_channels: isDisabled
          ? disabledChannels.filter((candidate) => candidate !== channel)
          : [...disabledChannels, channel],
      };
    });
  };

  const moveGroupChannel = (group: string, index: number, direction: -1 | 1) => {
    setGroupPolicy(group, (current) => ({
      ...current,
      channel_priority: moveItem(current.channel_priority, index, index + direction),
    }));
  };

  const updateGroupPolicyNumber = (
    group: string,
    field: 'max_fallback_depth' | 'cooldown_minutes',
    value: number,
  ) => {
    setGroupPolicy(group, (current) => ({
      ...current,
      [field]: value,
    }));
  };

  const toggleGroupFallbackRule = (group: string, trigger: string, enabled: boolean) => {
    setGroupPolicy(group, (current) => {
      const existing = current.fallback_rules.find((rule) => rule.trigger === trigger);
      if (enabled && !existing) {
        return {
          ...current,
          fallback_rules: [...current.fallback_rules, defaultFallbackRule(trigger)],
        };
      }
      if (!enabled && existing) {
        return {
          ...current,
          fallback_rules: current.fallback_rules.filter((rule) => rule.trigger !== trigger),
        };
      }
      return current;
    });
  };

  const updateGroupFallbackRule = (group: string, trigger: string, patch: Partial<FallbackRule>) => {
    setGroupPolicy(group, (current) => {
      const ruleIndex = current.fallback_rules.findIndex((rule) => rule.trigger === trigger);
      const nextRules = [...current.fallback_rules];
      if (ruleIndex >= 0) {
        nextRules[ruleIndex] = {
          ...nextRules[ruleIndex],
          ...patch,
          trigger,
          description: FALLBACK_RULE_DESCRIPTIONS[trigger] || nextRules[ruleIndex].description,
          next_channel: patch.next_channel ?? (nextRules[ruleIndex].next_channel || 'auto'),
        };
      } else {
        nextRules.push({
          ...defaultFallbackRule(trigger),
          ...patch,
          trigger,
        });
      }
      return { ...current, fallback_rules: nextRules };
    });
  };

  const handleClinicDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleClinicDragOver = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    if (draggedIndex === null || draggedIndex === index) {
      return;
    }
    updateSettings({
      channel_priority: moveItem(settings.channel_priority, draggedIndex, index),
    });
    setDraggedIndex(index);
  };

  const handleClinicDragEnd = () => {
    setDraggedIndex(null);
  };

  const renderClinicDeliveryFlow = () => {
    const notReadRule = settings.fallback_rules.find((rule) => rule.trigger === 'delivered_not_read');
    const notDeliveredRule = settings.fallback_rules.find((rule) => rule.trigger === 'sent_not_delivered');
    const actionRule = settings.fallback_rules.find((rule) => rule.trigger === 'action_not_completed');

    return (
      <Card className="bg-gradient-to-b from-slate-50 to-white border-2 border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Схема доставки сообщений клиники</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex flex-col items-center gap-2">
            {settings.channel_priority
              .filter((channel) => !(settings.disabled_channels || []).includes(channel))
              .slice(0, settings.max_fallback_depth)
              .map((channel, index, enabledChannels) => {
                const meta = CHANNEL_METADATA[channel] || { name: channel, hasTracking: false };
                const isLast = index === enabledChannels.length - 1;

                return (
                  <div key={channel} className="w-full max-w-md">
                    <div
                      className={`flex items-center justify-between rounded-lg border-2 p-3 ${
                        index === 0 ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Badge variant={index === 0 ? 'success' : 'secondary'} className="text-xs">
                          {index + 1}
                        </Badge>
                        <span className="font-medium">{meta.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        {meta.hasTracking ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <Check className="h-3 w-3" /> read tracking
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-orange-500">
                            <AlertTriangle className="h-3 w-3" /> sent only
                          </span>
                        )}
                      </div>
                    </div>

                    {!isLast && (
                      <div className="flex flex-col items-center py-2">
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <div className="flex items-center gap-1 rounded bg-red-50 px-2 py-1">
                            <X className="h-3 w-3 text-red-500" />
                            <span>hard fail</span>
                          </div>
                          {meta.hasTracking && notReadRule && (
                            <div className="flex items-center gap-1 rounded bg-amber-50 px-2 py-1">
                              <Clock className="h-3 w-3 text-amber-500" />
                              <span>не прочитано {notReadRule.timeout_hours}ч</span>
                            </div>
                          )}
                          {meta.hasTracking && notDeliveredRule && (
                            <div className="flex items-center gap-1 rounded bg-orange-50 px-2 py-1">
                              <Clock className="h-3 w-3 text-orange-500" />
                              <span>не доставлено {notDeliveredRule.timeout_hours}ч</span>
                            </div>
                          )}
                          {actionRule && (
                            <div className="flex items-center gap-1 rounded bg-blue-50 px-2 py-1">
                              <Clock className="h-3 w-3 text-blue-500" />
                              <span>нет реакции на CTA {actionRule.timeout_hours}ч</span>
                            </div>
                          )}
                        </div>
                        <ArrowDown className="my-1 h-5 w-5 text-slate-400" />
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Схема выше показывает clinic default policy. Точный resolved канал и patient-specific причины смотрите в preview элемента очереди.
          </p>
        </CardContent>
      </Card>
    );
  };

  const renderClinicPriorityEditor = () => (
    <Card>
      <CardHeader>
        <CardTitle>Приоритет каналов клиники</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <p className="mb-4 text-sm text-neutral-600">
          Перетащите каналы для изменения clinic default priority. Первый активный канал используется для новых уведомлений, если у пациента нет `preferred_channel`.
        </p>
        <div className="space-y-2">
          {settings.channel_priority.map((channel, index) => {
            const meta = CHANNEL_METADATA[channel] || { name: channel, hasTracking: false };
            const enabled = !(settings.disabled_channels || []).includes(channel);

            return (
              <div
                key={channel}
                draggable={enabled}
                onDragStart={() => enabled && handleClinicDragStart(index)}
                onDragOver={(event) => enabled && handleClinicDragOver(event, index)}
                onDragEnd={handleClinicDragEnd}
                className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${
                  enabled ? 'cursor-move border-shell-border bg-shell-surface hover:bg-shell-hover' : 'cursor-default border-shell-danger/30 bg-shell-danger-soft opacity-70'
                } ${draggedIndex === index ? 'bg-shell-elevated opacity-50' : ''}`}
              >
                <button
                  type="button"
                  onClick={() => toggleClinicChannelEnabled(channel)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    enabled ? 'bg-green-500' : 'bg-red-400'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <GripVertical className={`h-5 w-5 ${enabled ? 'text-neutral-400' : 'text-neutral-300'}`} />
                <span className={`font-medium ${enabled ? '' : 'line-through text-red-500'}`}>
                  {index + 1}. {meta.name}
                </span>
                <span className="flex-1" />
                {enabled ? (
                  <>
                    <span className="text-sm text-neutral-500">
                      {meta.hasTracking ? 'delivered/read' : 'sent only'}
                    </span>
                    {!meta.hasTracking && meta.defaultTimeout && (
                      <span className="text-sm text-neutral-500">timeout {meta.defaultTimeout}ч</span>
                    )}
                    {index >= settings.max_fallback_depth && (
                      <Badge variant="secondary" className="text-xs">
                        не используется
                      </Badge>
                    )}
                  </>
                ) : (
                  <Badge variant="error" className="text-xs">
                    Отключён
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  const renderClinicFallbackEditor = () => (
    <Card>
      <CardHeader>
        <CardTitle>Clinic default fallback</CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div>
          <h3 className="mb-3 text-sm font-medium text-neutral-700">Async fallback по таймаутам</h3>
          <div className="space-y-4 pl-4">
            {['delivered_not_read', 'sent_not_delivered', 'action_not_completed'].map((trigger) => {
              const rule = settings.fallback_rules.find((candidate) => candidate.trigger === trigger) || defaultFallbackRule(trigger);
              return (
                <div key={trigger} className="flex items-center gap-4">
                  <span className="w-56 text-sm text-neutral-600">{rule.description}</span>
                  <Input
                    type="number"
                    value={rule.timeout_hours}
                    onChange={(event) =>
                      updateClinicFallbackRule(trigger, { timeout_hours: parseNumericInput(event.target.value, rule.timeout_hours) })
                    }
                    className="w-20"
                  />
                  <span className="text-sm text-neutral-500">ч → следующий доступный канал</span>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-sm font-medium text-neutral-700">Immediate fallback на hard fail</h3>
          <div className="space-y-4 pl-4">
            <div className="flex items-center gap-4">
              <span className="w-56 text-sm text-neutral-600">Provider/send error</span>
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                Сразу → следующий доступный канал
              </div>
              <span className="text-sm text-neutral-500">контролируется правилом `failed`</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-56 text-sm text-neutral-600">Максимум каналов в цепочке</span>
              <Input
                type="number"
                value={settings.max_fallback_depth}
                onChange={(event) =>
                  updateSettings({ max_fallback_depth: parseNumericInput(event.target.value, settings.max_fallback_depth) })
                }
                className="w-20"
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="w-56 text-sm text-neutral-600">Минимум минут между fallback</span>
              <Input
                type="number"
                value={settings.cooldown_minutes}
                onChange={(event) =>
                  updateSettings({ cooldown_minutes: parseNumericInput(event.target.value, settings.cooldown_minutes) })
                }
                className="w-20"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderQuietHours = () => (
    <Card>
      <CardHeader>
        <CardTitle>Тихий режим</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={settings.quiet_hours.enabled}
              onChange={(event) => updateQuietHours('enabled', event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-neutral-700">Включить тихий режим</span>
          </label>
          {settings.quiet_hours.enabled && (
            <div className="flex items-center gap-4 pl-6">
              <span className="text-sm text-neutral-600">Не отправлять с</span>
              <Input
                type="number"
                min="0"
                max="23"
                value={settings.quiet_hours.start}
                onChange={(event) => updateQuietHours('start', parseNumericInput(event.target.value, settings.quiet_hours.start))}
                className="w-20"
              />
              <span className="text-sm text-neutral-600">до</span>
              <Input
                type="number"
                min="0"
                max="23"
                value={settings.quiet_hours.end}
                onChange={(event) => updateQuietHours('end', parseNumericInput(event.target.value, settings.quiet_hours.end))}
                className="w-20"
              />
              <span className="text-sm text-neutral-500">(Europe/Moscow)</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderGroupPolicyEditor = (group: { value: string; label: string }) => {
    const { policy, isOverride } = getEffectiveGroupPolicy(group.value);
    const policyChannels = Array.from(new Set([...policy.channel_priority, ...DELIVERY_CHANNELS]));

    const expanded = openGroup === group.value;
    return (
      <div key={group.value} className={fallbackCanvas.group}>
        <div className="flex items-center justify-between gap-4 px-4 py-4">
          <button
            type="button"
            className="min-w-0 text-left"
            onClick={() => setOpenGroup(expanded ? null : group.value)}
          >
            <p className="font-medium text-shell-strong">{group.label}</p>
            <p className={fallbackCanvas.summary}>{group.value}</p>
          </button>
          <div className="flex items-center gap-2">
            <Badge variant={isOverride ? 'success' : 'secondary'} className="text-xs">
              {isOverride ? 'Своя цепочка' : 'Как у клиники'}
            </Badge>
            <Button
              variant={isOverride ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => {
                if (isOverride) {
                  resetGroupPolicyOverride(group.value);
                  return;
                }
                setGroupPolicy(group.value, (current) => current);
                onSectionChange('own');
                setOpenGroup(group.value);
              }}
            >
              {isOverride ? 'Вернуть каналы клиники' : 'Своя цепочка'}
            </Button>
          </div>
        </div>

        {expanded ? (
        <div className="space-y-4 border-t border-shell-border px-4 py-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-sm text-neutral-700">Режим группы</p>
              <DispatchModeBadge mode={settings.broadcast_group_modes?.[group.value] || 'manual'} />
            </div>
            <select
              className="w-full rounded-md border border-shell-border bg-shell-surface px-3 py-2 text-sm text-shell-text"
              value={settings.broadcast_group_modes?.[group.value] || 'manual'}
              onChange={(event) => updateBroadcastGroupMode(group.value, event.target.value)}
            >
              <option value="manual">Вручную</option>
              <option value="auto">Автоматом</option>
            </select>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-neutral-700">Дневной лимит группы</p>
            <Input
              type="number"
              value={settings.broadcast_group_daily_limits?.[group.value] ?? 0}
              onChange={(event) => updateBroadcastGroupLimit(group.value, parseNumericInput(event.target.value, 0))}
            />
          </div>
          <label className="flex items-center gap-2 pt-7 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={disabledFallbackGroups.includes(group.value)}
              onChange={() => toggleFallbackDisabledGroup(group.value)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Запретить fallback между каналами
          </label>
        </div>

        <div className="rounded-2xl border border-shell-border bg-shell-elevated px-3 py-3 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-shell-text">Цепочка каналов группы</span>
            <Badge variant={isOverride ? 'success' : 'secondary'} className="text-xs">
              {isOverride ? 'своя' : 'каналы клиники'}
            </Badge>
          </div>
          {group.value === 'feedback_request' && (
            <p className="text-xs text-shell-primary-strong">
              Для feedback-request можно задать отдельную messenger-first цепочку, например `MAX → Telegram Bot → WhatsApp WABA → WhatsApp Regular → SMS`.
              Override применяется поверх clinic default и сразу виден в `/settings`.
            </p>
          )}
          <p className="text-xs text-shell-muted">
            Каналы: {policy.channel_priority.join(' -> ')}
          </p>
          <p className="text-xs text-shell-muted">
            Fallback: {policy.fallback_rules.length > 0
              ? policy.fallback_rules.map(formatFallbackRuleSummary).join(' | ')
              : 'нет правил'}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-3">
            <p className="text-sm font-medium text-shell-text">Приоритет каналов группы</p>
            <div className="space-y-2">
              {policy.channel_priority.map((channel, index) => {
                const enabled = !(policy.disabled_channels || []).includes(channel);
                const meta = CHANNEL_METADATA[channel] || { name: channel, hasTracking: false };
                return (
                  <div key={channel} className="flex items-center gap-3 rounded-2xl border border-shell-border bg-shell-surface p-3">
                    <button
                      type="button"
                      onClick={() => toggleGroupChannelEnabled(group.value, channel)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        enabled ? 'bg-green-500' : 'bg-red-400'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`font-medium ${enabled ? '' : 'line-through text-red-500'}`}>
                      {index + 1}. {meta.name}
                    </span>
                    <span className="flex-1" />
                    <Button variant="ghost" size="sm" disabled={index === 0} onClick={() => moveGroupChannel(group.value, index, -1)}>
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={index === policy.channel_priority.length - 1}
                      onClick={() => moveGroupChannel(group.value, index, 1)}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-neutral-700">Fallback rules группы</p>
            <div className="space-y-3">
              {FALLBACK_RULE_ORDER.map((trigger) => {
                const rule = policy.fallback_rules.find((candidate) => candidate.trigger === trigger);
                const isEnabled = Boolean(rule);
                const currentRule = rule || defaultFallbackRule(trigger);
                return (
                  <div key={trigger} className="rounded-md border bg-white p-3 space-y-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-neutral-700">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(event) => toggleGroupFallbackRule(group.value, trigger, event.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      {FALLBACK_RULE_DESCRIPTIONS[trigger] || trigger}
                    </label>
                    {isEnabled && (
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {trigger !== 'failed' && (
                          <div className="space-y-1">
                            <p className="text-xs text-neutral-500">Таймаут, часов</p>
                            <Input
                              type="number"
                              value={currentRule.timeout_hours}
                              onChange={(event) =>
                                updateGroupFallbackRule(group.value, trigger, {
                                  timeout_hours: parseNumericInput(event.target.value, currentRule.timeout_hours),
                                })
                              }
                            />
                          </div>
                        )}
                        <div className="space-y-1">
                          <p className="text-xs text-neutral-500">Следующий канал</p>
                          <select
                            className="w-full rounded-md border px-3 py-2 text-sm"
                            value={currentRule.next_channel || 'auto'}
                            onChange={(event) => updateGroupFallbackRule(group.value, trigger, { next_channel: event.target.value })}
                          >
                            <option value="auto">auto</option>
                            {policyChannels.map((channel) => (
                              <option key={channel} value={channel}>
                                {CHANNEL_METADATA[channel]?.name || channel}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <p className="text-sm text-neutral-700">Максимум каналов</p>
                <Input
                  type="number"
                  value={policy.max_fallback_depth}
                  onChange={(event) =>
                    updateGroupPolicyNumber(group.value, 'max_fallback_depth', parseNumericInput(event.target.value, policy.max_fallback_depth))
                  }
                />
              </div>
              <div className="space-y-2">
                <p className="text-sm text-neutral-700">Cooldown, минут</p>
                <Input
                  type="number"
                  value={policy.cooldown_minutes}
                  onChange={(event) =>
                    updateGroupPolicyNumber(group.value, 'cooldown_minutes', parseNumericInput(event.target.value, policy.cooldown_minutes))
                  }
                />
              </div>
            </div>
          </div>
        </div>
        </div>
        ) : null}
      </div>
    );
  };

  const ownGroups = broadcastGroups.filter((group) => getEffectiveGroupPolicy(group.value).isOverride);
  const sharedGroups = broadcastGroups.filter((group) => !getEffectiveGroupPolicy(group.value).isOverride);

  const renderSharedGroup = (group: { value: string; label: string }) => {
    const mode = settings.broadcast_group_modes?.[group.value] || 'manual';
    return (
      <div key={group.value} className={fallbackCanvas.group}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium text-shell-strong">{group.label}</p>
              <DispatchModeBadge mode={mode} />
            </div>
            <p className={fallbackCanvas.summary}>Каналы клиники · {group.value}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="h-10 rounded-lg border border-shell-border bg-shell-surface px-3 text-sm text-shell-text"
              value={mode}
              onChange={(event) => updateBroadcastGroupMode(group.value, event.target.value)}
            >
              <option value="manual">Вручную</option>
              <option value="auto">Автоматом</option>
            </select>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setGroupPolicy(group.value, (current) => current);
                setOpenGroup(group.value);
                onSectionChange('own');
              }}
            >
              Своя цепочка
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={fallbackCanvas.stack}>
      {section === 'clinic' && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2 rounded-xl border border-shell-border bg-shell-surface p-4">
              <p className={fallbackCanvas.fieldLabel}>Общий дневной лимит рассылок</p>
              <Input
                type="number"
                value={settings.broadcast_daily_limit || 0}
                onChange={(event) => updateSettings({ broadcast_daily_limit: parseNumericInput(event.target.value, 0) })}
              />
              <p className={fallbackCanvas.hint}>0 вернёт лимит по умолчанию, −1 отключит общий лимит.</p>
            </div>
            <div className="space-y-2 rounded-xl border border-shell-border bg-shell-surface p-4">
              <p className={fallbackCanvas.fieldLabel}>Напоминание за 24 часа</p>
              <p className="text-sm text-shell-text">Крон запускается в 10:00 по времени сервера.</p>
              <p className={fallbackCanvas.hint}>Слот зафиксирован в коде: напоминание на завтра уходит сегодня.</p>
            </div>
          </div>
          {renderClinicDeliveryFlow()}
          {renderClinicPriorityEditor()}
          {renderClinicFallbackEditor()}
          {renderQuietHours()}
        </>
      )}

      {section !== 'clinic' && (
        <div className="space-y-3">
          {(section === 'own' ? ownGroups : sharedGroups).length === 0 ? (
            <p className={fallbackCanvas.hint}>
              {section === 'own' ? 'Ни у одной группы нет своей цепочки каналов.' : 'Все группы задают цепочку сами.'}
            </p>
          ) : section === 'shared' ? (
            sharedGroups.map(renderSharedGroup)
          ) : (
            ownGroups.map(renderGroupPolicyEditor)
          )}
        </div>
      )}
    </div>
  );
}
